import React from 'react';
import { Button } from 'antd';
import moment from 'moment';
import { connect } from 'react-redux';

import { selectors as accountListSelectors } from '../../../../slices/accounts/list';
import { selectors as authSelectors } from '../../../../slices/auth';
import { RootState } from '../../../../slices/rootReducer';
import { AccessTypes } from '../../../common/domain/accountAccessType';
import { accountTypeNames } from '../common/constants';
import { AddSeatsModal } from '../common/addSeatsModal';
import { Features, isFeatureEnabled } from '../../../../featureToggles/features';
import { BlockedByFeature, BlockedByFeatureType } from '../../../../featureToggles/features/utils';
import { SxTooltip } from '../../../common/utils/wrapComponents';
import { FeatureToggle, Off, On } from '../../../../featureToggles';
import { AddSeatsEnterpriseModal } from '../common/AddSeatsEnterpriseModal';

import styles from './index.module.scss';

const isTrialsOfTiersEnabled = isFeatureEnabled(Features.TrialsOfTiers);

interface EnterpriseSubscriptionProps {
  canAddSeats: boolean;
  // use for Features.TrialsOfTiers
  isBillingInfoMissing: boolean | BlockedByFeatureType;
  isSubscriptionInactive: boolean | BlockedByFeatureType;
  // end of use for Features.TrialsOfTiers
  currentSeats?: number;
  renewalDate?: Date;
}

interface EnterpriseSubscriptionState {
  addSeatsModalOpen: boolean;
}

class EnterpriseSubscriptionBase extends React.Component<EnterpriseSubscriptionProps, EnterpriseSubscriptionState> {
  constructor(props: EnterpriseSubscriptionProps) {
    super(props);
    this.state = {
      addSeatsModalOpen: false
    };
    this.toggleModalOpen = this.toggleModalOpen.bind(this);
  }

  private toggleModalOpen() {
    this.setState(state => ({ addSeatsModalOpen: !state.addSeatsModalOpen }));
  }

  private renderModal() {
    const { currentSeats } = this.props;
    const { addSeatsModalOpen } = this.state;
    if (!addSeatsModalOpen) {
      return null;
    }
    return <FeatureToggle feature={Features.TrialsOfTiers}>
      <On>
        <AddSeatsEnterpriseModal
          onCancel={this.toggleModalOpen}
        />
      </On>
      <Off>
        <AddSeatsModal
          onClose={this.toggleModalOpen}
          currentSeats={currentSeats ?? 0}
        />
      </Off>
    </FeatureToggle>;
  }

  private renderSeatsInfo() {
    const { canAddSeats, currentSeats, isBillingInfoMissing, isSubscriptionInactive } = this.props;
    if (!canAddSeats) {
      return null;
    }
    const noBillingInfo = isBillingInfoMissing !== BlockedByFeature && isBillingInfoMissing;
    const noActiveSubscription = isSubscriptionInactive !== BlockedByFeature && isSubscriptionInactive;
    const shouldDisplayTooltip = isTrialsOfTiersEnabled && (noBillingInfo || noActiveSubscription);
    const addSeatsButton = (
      <Button
        type="primary"
        disabled={shouldDisplayTooltip}
        className={styles.button}
        onClick={this.toggleModalOpen}
      >
        Add seats
      </Button>
    );

    const tooltipText = noActiveSubscription ? 'You can\'t add new seats until your lapsed subscription is renewed. Contact your SightX rep for assistance.' :
      'We\'ve encountered a problem with your billing information. Please contact your SightX rep to resolve this.';
    const tooltip = (
      <SxTooltip
        Component={addSeatsButton}
        tooltipText={tooltipText}
      />
    );

    return <>
      <div className={styles.seats}>Current seats: {currentSeats}</div>
      {shouldDisplayTooltip ? tooltip : addSeatsButton}
      {this.renderModal()}
    </>;
  }

  render() {
    const { renewalDate } = this.props;
    const formattedDate = moment(renewalDate).format('MMMM DD, YYYY'); // only for feature toggle :(
    const displayedDate = renewalDate ? formattedDate : '--';
    return (
      <div className={styles.container}>
        <span className={styles.badge}>Current plan</span>
        <div className={styles.title}>{accountTypeNames[AccessTypes.Enterprise]}</div>
        <div className={styles.description}>Subscription renews {isTrialsOfTiersEnabled ? displayedDate : formattedDate}</div>
        {this.renderSeatsInfo()}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): EnterpriseSubscriptionProps => {
  const user = authSelectors.getLoggedInUser(state);
  const account = accountListSelectors.getAccountById(state, user?.account.id || '');
  const currentSeats = account?.seatsPurchased;
  const oldRenewalDate = account && account.subscription ? new Date(account.subscription.cycleEndDate * 1000) : undefined;
  const renewalDate = isTrialsOfTiersEnabled ? account?.renewalAt : oldRenewalDate;
  const isBillingInfoMissing = isTrialsOfTiersEnabled ? !account?.billingAddress : BlockedByFeature;
  let isSubscriptionInactive: BlockedByFeatureType | boolean = BlockedByFeature;
  if (isTrialsOfTiersEnabled && renewalDate) {
    const now = moment.utc();
    const renewalDateUTC = moment.utc(renewalDate);
    isSubscriptionInactive = renewalDateUTC.diff(now, 'hours') < 1;
  }

  return {
    canAddSeats: isTrialsOfTiersEnabled ? authSelectors.isAuthUserAdminOrOwner(state) : false,
    currentSeats,
    renewalDate,
    isBillingInfoMissing,
    isSubscriptionInactive
  };
};

export const EnterpriseSubscription = connect(mapStateToProps)(EnterpriseSubscriptionBase);